import { DataTable } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Blocks } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { type ExtensionSummary, listExtensionsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { AdminConnectorsSection } from "./AdminConnectorsSection";
import { ConnectorActivitySection } from "./ConnectorActivitySection";
import { ConnectorPolicySection } from "./ConnectorPolicySection";
import { useIntegrationColumns } from "./integrationColumns";
import { isBuiltIn } from "./setupStatus";

/** /admin/integrations: the catalog, read-only for now (docs/design/web/WEB_EXTENSIONS_PLAN.md §5). */
export function IntegrationsPage() {
  const { t } = useTranslation("adminIntegrations");
  const navigate = useNavigate();
  const canEdit = useAdminCanEdit();
  const extensions = useQuery({ ...listExtensionsOptions(), select: (r) => r.data });
  const columns = useIntegrationColumns();
  const all = extensions.data ?? [];
  const open = (extension: ExtensionSummary) => navigate(`/admin/integrations/${extension.uuid}`);

  const table = (label: string, rows: ExtensionSummary[]) => (
    <DataTable
      aria-label={label}
      columns={columns}
      rows={rows}
      rowKey={(extension) => extension.uuid}
      loading={extensions.isPending}
      onRowClick={open}
      empty={<EmptyState icon={<Blocks size={32} />} title={t("empty")} body={t("emptyBody")} />}
    />
  );

  return (
    <AdminPage title={t("title")} subtitle={t("subtitle")}>
      {extensions.isError ? (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      ) : (
        <>
          <PageSection title={t("sections.integrations")} description={t("sections.integrationsBody")}>
            {table(
              t("tableLabel"),
              all.filter((e) => !isBuiltIn(e)),
            )}
          </PageSection>
          <PageSection title={t("sections.builtIn")} description={t("sections.builtInBody")}>
            {table(t("builtInTableLabel"), all.filter(isBuiltIn))}
          </PageSection>
          <ConnectorPolicySection canEdit={canEdit} />
          <AdminConnectorsSection canEdit={canEdit} />
          <ConnectorActivitySection />
        </>
      )}
    </AdminPage>
  );
}
